import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

// Angular source: apps/angular/src/js/ng/parent/bookings/modals/multiple-sitter-modal.*
// Shown when a booking's date+time duplicates an existing booking group —
// asks whether to book the first sitter to accept, or pick a specific count
// (which defers to SittersNeededModal).
interface Props {
  isOpen: boolean;
  onClose: () => void;
  groupBookings: Array<{ caregiver?: { pictureUrl?: string } }>;
  sitter?: { pictureUrl?: string };
  onFirstAccepter: () => void;
  onPickCount: () => void;
}

export const MultipleSitterModal = ({
  isOpen,
  onClose,
  groupBookings,
  sitter,
  onFirstAccepter,
  onPickCount,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Modal isOpen={isOpen} onClose={onClose} className='w-full max-w-lg'>
      <Button
        aria-label={t('common.close')}
        onClick={onClose}
        className='absolute right-2 top-2 text-gray-400 hover:text-gray-500 transition-colors'
      >
        <FontAwesomeIcon icon={faXmark} className='w-5 h-5' />
      </Button>
      <div className='font-semibold mb-3'>{t('components.modals.multipleSitter.header')}</div>
      <h2 className='mb-3'>{t('components.modals.multipleSitter.question')}</h2>
      <div className='flex gap-2 mb-3'>
        {groupBookings[0]?.caregiver?.pictureUrl && (
          <img
            src={groupBookings[0].caregiver.pictureUrl}
            alt=''
            className='w-20 h-20 rounded object-cover'
          />
        )}
        {sitter?.pictureUrl && (
          <img
            src={sitter.pictureUrl}
            alt=''
            className='w-20 h-20 rounded object-cover'
          />
        )}
      </div>
      <p className='bg-neutral-100 rounded p-3 mb-4 text-sm'>
        {t('components.modals.multipleSitter.info')}
      </p>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <Button
          id='book-one-sitter'
          buttonType={ButtonType.primary}
          className='w-full'
          onClick={onFirstAccepter}
        >
          {t('components.modals.multipleSitter.firstAccepter')}
        </Button>
        <Button
          id='book-multiple-sitters'
          buttonType={ButtonType.secondary}
          className='w-full'
          onClick={onPickCount}
        >
          {t('components.modals.multipleSitter.multipleNeeded')}
        </Button>
      </div>
    </Modal>
  );
};
